<script>
import {
  defineComponent, reactive, ref, onMounted,
} from 'vue';
import { mask } from 'vue-the-mask';
import { checkUserExists } from '@/graphs/gql';
import shared from '../../shared';

// Cache implementation
export default defineComponent({
  name: 'Authenticator',
  directives: { mask },
  setup() {
    const {
      route,
      router,
      store,
      makeGraphRequest,
    } = shared();
    const item = reactive({
      phone: '',
      email: '',
    });
    const loading = ref(false);
    const refForm = ref(null);
    const error = ref(false);
    const errorMessage = ref();
    const returnTo = ref(route.query.returnTo || 'web');

    const rules = {};

    const phoneInput = ref(null);
    const emailInput = ref(null);

    function checkAuth() {
      if (store.getters['users/isMeSignedIn']) {
        if (route.path !== '/') router.push('/');
      }
    }
    async function submitForm() {
      try {
        loading.value = true;
        error.value = false;
        errorMessage.value = null;
        let response = null;
        let authType = null;

        if (item.phone !== '' || item.email !== '') {
          if (item.phone !== '') {
            authType = 'phone';
            response = await makeGraphRequest('query', checkUserExists, { phone: item.phone }, null);
          } else if (item.email !== '') {
            authType = 'email';
            response = await makeGraphRequest('query', checkUserExists, { email: item.email }, null);
          }

          if (response && response.data && response.data.checkUserExists) {
            router.push(`/auth/forgot-password/${response.data.checkUserExists.id}/verify-resetcode?authType=${authType}&returnTo=${returnTo.value}`);
          } else {
            error.value = true;
            errorMessage.value = 'Account Does not Exist';
          }
        } else {
          error.value = true;
          errorMessage.value = 'Please enter your phone number or email to reset the password.';
        }
      } catch (err) {
        error.value = true;
        errorMessage.value = 'Account Does not Exist';
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    function validateForm() {
      if (item.phone !== '' || item.email !== '') {
        submitForm();
      } else {
        error.value = true;
        errorMessage.value = 'Please enter your phone number or email to reset the password.';
      }
    }

    onMounted(() => checkAuth());

    return {
      refForm,
      rules,
      loading,
      error,
      errorMessage,
      item,
      emailInput,
      phoneInput,
      validateForm,
    };
  },
});
</script>

<template>

  <el-form
    ref="refForm"
    :rules="rules"
    :model="item"
    status-icon
    label-position="top"
    class="c-forgot-password"
  >

    <section>
      <div class="c-forgot-password__heading">Forgot Password</div>
      <div class="c-forgot-password__subheading">Enter your phone or email to reset your password</div>
    </section>

    <section class="c-forgot-password__items">
      <el-form-item prop="phone" @click="phoneInput.focus()">
        <label><span class="required-star">*</span> Phone</label>
        <el-input
          v-model="item.phone"
          type="tel"
          placeholder="(123) 456-7890"
          ref="phoneInput"
          v-mask="'(###) ###-####'"
        />
        <div v-if="error && !item.email && !item.phone" class="error-message">
          Please enter your phone number
        </div>
        <div v-if="error && item.phone && errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </el-form-item>

      <div class="c-forgot-password__separator">OR</div>

      <el-form-item prop="email" @click="emailInput.focus()">
        <label><span class="required-star">*</span> Email</label>
        <el-input
          v-model="item.email"
          type="email"
          placeholder="abcd@spotlight.com"
          ref="emailInput"
        />
        <div v-if="error && !item.phone && !item.email" class="error-message">
          Please enter your email
        </div>
        <div v-if="error && item.email && errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </el-form-item>
    </section>

    <el-button class="c-forgot-password__submit" round type="success" @click="validateForm" v-loading="loading">
      Reset Password
    </el-button>
  </el-form>

</template>

<style lang="scss">
.c-forgot-password {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .required-star {
    color: #F56C6C;
  }

  .error-message {
    color: #F56C6C;
    font-size: 12px;
    margin-left: -20px;
    margin-top: 5px;
  }

  .el-form-item {
    margin-bottom: 0;
  }

  // Стили для удаления бордеров у el-input
  .el-input__wrapper {
    box-shadow: none !important;
    padding: 0;
  }

  .el-input__inner {
    border: none;
  }

  // Стили для состояния фокуса
  .el-input__wrapper.is-focus {
    box-shadow: none !important;
  }

  // Стили для плейсхолдера
  .el-input__inner::placeholder {
    opacity: 0.5;
  }

  &__heading, &__subheading {
    font-family: 'Red Hat Display';
    text-align: center;
  }

  &__heading {
    color: #333333;
    font-size: 30px;
    font-weight: bold;
  }

  &__subheading {
    color: #666666;
    font-size: 14px;
    font-weight: 400;
    margin-top: 8px;
  }

  &__separator {
    text-align: center;
    margin: 10px 0;
    color: #666666;
    font-weight: bold;
  }

  &__submit.el-button.el-button--success {
    width: 100%;
    height: 48px;
    font-size: 16px;
    border-radius: 56px;
    background-color: #005C6B;
    border-color: #005C6B;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .el-form-item__content {
      height: 75px;
      border: 1px solid #DEDEDE;
      border-radius: 10px;
      padding: 20px;
      display: flex;
      flex-direction: column;

      label {
        font-family: "Red Hat Display";
        font-size: 12px;
        color: #666666;
        line-height: 12px;
        font-weight: 400;
      }
    }
  }
}

</style>
